import type { Role } from '@/features/auth/api';

export interface MenuChild {
  label: string;
  to: string;
  badge?: BadgeKey;
}

export interface MenuItem {
  label: string;
  to: string;
  /** サブメニュー（ホバー/クリックで展開 / 指示書 §7） */
  children?: MenuChild[];
  /**
   * 件数を赤丸で出すキー（D-099）。
   * 実際の件数は `/dashboard` から取得して AdminShell が渡す。
   */
  badge?: BadgeKey;
}

/** サイドバーに赤丸を出せる項目。値は取得元（DashboardSummary）のフィールドに対応する */
export type BadgeKey =
  | 'messages'
  | 'notifications'
  | 'inbox'
  | 'followUps'
  | 'eventTodos'
  | 'invitationsStudent'
  | 'invitationsDirector'
  | 'invitationsSuccess'
  | 'invitationsOffice'
  | 'invitationsMembers';

/**
 * 赤丸が何の件数かを読み上げ・ツールチップに出す（D-145）。
 * メッセージ系は「読めば消える」未読、フォローアップは作業キューなので「未対応」。
 */
export const BADGE_LABELS: Record<BadgeKey, string> = {
  messages: '未読',
  notifications: '未読',
  inbox: '未読',
  followUps: '未対応',
  eventTodos: '未対応',
  invitationsStudent: '未受諾',
  invitationsDirector: '未受諾',
  invitationsSuccess: '未受諾',
  invitationsOffice: '未受諾',
  invitationsMembers: '未受諾',
};

/**
 * ロール別サイドバーメニュー（指示書 §10 の画面エリアに対応）。
 * ここはあくまで導線であって権限判定ではない。**認可は API 側**（§0-6）。
 * 未実装のマイルストーンの項目は順次ここへ足していく。
 */
export const MENU_BY_ROLE: Record<Role, MenuItem[]> = {
  student: [
    { label: 'ダッシュボード', to: '/' },
    { label: 'イベント', to: '/events', badge: 'eventTodos' },
    { label: '採点結果', to: '/scores' },
    // 受講生同士の相互閲覧（2026-08-18 先方回答 / D-105）。公開項目のみAPI側で絞る
    { label: '受講生一覧', to: '/members' },
    { label: '社長からの期待', to: '/president' },
    { label: 'マイプロフィール', to: '/profile' },
    // 1年分の活動記録を1枚で見る（D-113）
    { label: '年間ポートフォリオ', to: '/portfolio' },
    { label: 'メッセージ', to: '/messages', badge: 'messages' },
    { label: 'システム通知', to: '/notifications', badge: 'notifications' },
    { label: '操作マニュアル', to: '/help' },
  ],
  director: [
    { label: 'ダッシュボード', to: '/' },
    // 種別ごとのサブメニューは Sidebar が注入（D-297。事務局のイベント管理と同じ）
    {
      label: '担当イベント',
      to: '/events',
      children: [{ label: '種別を選ぶ', to: '/events' }],
    },
    // 担当したイベントの対象会員（D-294）。年間ポートフォリオは一覧から（D-330）
    { label: '関わった会員', to: '/members' },
    // 事務局チャット＋担当イベントのグループ（D-299）
    { label: 'メッセージ', to: '/messages', badge: 'messages' },
    { label: '操作マニュアル', to: '/help' },
  ],
  // イベント一覧を軸にしたシンプルな構成（D-182）。
  // 種別ごとのサブメニューは Sidebar が注入（D-297）。主導線はサクセス面談
  success: [
    { label: 'ダッシュボード', to: '/' },
    {
      label: 'イベント',
      to: '/events',
      children: [{ label: '種別を選ぶ', to: '/events' }],
    },
    // 面談の候補日。受講生はここで登録された日時から選んで確定する（D-183）
    { label: '実施可能日', to: '/availability' },
    // 担当イベントの対象会員（D-330）。詳細はプロフィールのみ、提出物等は年間ポートフォリオ
    { label: '関わった会員', to: '/members' },
    // フォローアップは一旦メニューから外す（正仕様書に無く、先方確認前 / D-300）
    // 事務局チャット＋担当イベントのグループ（D-299）
    { label: 'メッセージ', to: '/messages', badge: 'messages' },
    { label: '操作マニュアル', to: '/help' },
  ],
  office: [
    { label: 'ダッシュボード', to: '/' },
    {
      label: '会員管理',
      to: '/members',
      badge: 'invitationsMembers',
      children: [
        { label: '会員一覧', to: '/members', badge: 'invitationsStudent' },
        { label: '会員CSV取込', to: '/members/import' },
        { label: '理事・講師', to: '/directors', badge: 'invitationsDirector' },
        { label: 'サクセス担当', to: '/success-staff', badge: 'invitationsSuccess' },
        { label: '事務局アカウント', to: '/office-accounts', badge: 'invitationsOffice' },
        // 社長への聞き取りは会員1人ずつの情報なので会員管理の下に置く。
        // 使う機会が少なく、単独でグローバルに出すと目立ちすぎた（D-320）
        { label: '社長アンケートCSV取込', to: '/surveys/president/import' },
      ],
    },
    {
      label: 'イベント管理',
      to: '/events',
      badge: 'eventTodos',
      children: [
        { label: 'イベント一覧', to: '/events', badge: 'eventTodos' },
        { label: '繰返し作成', to: '/events/recurring/new' },
        // 一括CSVは種別ごとに列が違うため、1on1・サクセス面談は各一覧から（D-273・D-274・D-276）
      ],
    },
    {
      // 人とのやり取り（返信できるもの）。自動で送られるものは「システム通知」の側（D-136・D-137）。
      // 事務局の主業務は問い合わせへの対応なので、対応画面を先頭に置く
      label: 'メッセージ',
      to: '/inbox',
      // 親には未対応の問い合わせ件数を出す（畳んでいても気づけるように）
      badge: 'inbox',
      children: [
        { label: '問い合わせ対応', to: '/inbox', badge: 'inbox' },
        // 連絡先（会員・理事講師・サクセス担当）を選んでやり取りする画面（D-138）
        { label: '連絡先', to: '/messages', badge: 'messages' },
      ],
    },
    {
      // 事務局の管理画面で扱うのは「システムが何を送るか・送ったか」だけ。
      // 自分に届いたものはヘッダーのベルから見る（管理画面に受信箱を並べない / D-137）
      label: 'システム通知（配信予定・履歴）',
      to: '/notifications/schedules',
    },
    {
      label: '採点',
      to: '/scores',
      children: [
        { label: '順位一覧', to: '/scores' },
        { label: '採点管理（CSV・公開）', to: '/scores/manage' },
      ],
    },
    {
      label: '業務マスター',
      to: '/masters',
      children: [
        { label: 'マスタトップ', to: '/masters' },
        { label: '会員・組織（期）', to: '/masters/cohorts' },
        { label: '会員・組織（コース）', to: '/masters/courses' },
        { label: 'イベント種別', to: '/masters/event-types' },
        { label: 'アンケートテンプレート', to: '/masters/survey-templates' },
        // 「社長アンケート設定」は D-146 で廃止（設問を固定フィールドにしたため）
        { label: '席次NG', to: '/masters/ng-rules' },
        { label: '通知プリセット', to: '/masters/notification-presets' },
        { label: '通知テンプレート', to: '/masters/notification-templates' },
      ],
    },
    { label: '外部連携', to: '/integrations' },
    // データ移行画面は 2026-08-18 の先方回答（機能として不要）で撤去。
    // 過去データの投入は会員CSV・採点結果CSV等の既存取込で行う（D-104）
    { label: '監査ログ', to: '/audit-logs' },
    { label: '操作マニュアル', to: '/help' },
  ],
  super_admin: [
    { label: '運営ダッシュボード', to: '/' },
    { label: '事務局管理者', to: '/superadmin/office-admins' },
    { label: '外部連携（非常時の確認用）', to: '/superadmin/integrations' },
    { label: 'ジョブ・障害対応', to: '/superadmin/jobs' },
    { label: 'システム監査ログ', to: '/audit-logs' },
    { label: '操作マニュアル', to: '/help' },
  ],
};

export function menuFor(role: Role): MenuItem[] {
  return MENU_BY_ROLE[role] ?? [];
}
